/**
 * DTOs the app shell needs. Field names are camelCase, mirroring the API's
 * response DTOs (`apps/api/src/modules/*\/service.ts`), not the snake_case
 * columns of `apps/api/src/db/schema.ts` — the repository layer converts
 * before a row ever leaves the API, `db/values.ts` included for booleans.
 *
 * Feature screens add their own DTOs next to their views; only what the shell
 * itself consumes belongs here.
 */
import type {
  CategoryDirection,
  ContactKind,
  CountryScope,
  CustomerType,
  FiscalYearStatus,
  InvoiceTemplateId,
  MembershipRole,
  ProfitMethod,
  TaxationMethod,
  VatPeriodicity,
  VatScheme,
} from '@eua/shared'

/** Mirrors `core/pagination.ts`. */
export interface Page<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  pageCount: number
}

export interface CurrentUser {
  id: string
  email: string
  name: string
  locale: string
  isActive: boolean
  /** `null` until the confirmation link from the welcome mail was opened. */
  emailVerifiedAt: string | null
  lastLoginAt: string | null
  /**
   * Newest day of the Neuerungen list this user has seen, `YYYY-MM-DD`, or
   * `null` while they have never opened the page.
   */
  changelogSeenDate: string | null
  createdAt: string
}

/**
 * `/auth/login` from an unknown browser: no session yet — a six-digit code
 * went to the mailbox and has to come back via `/auth/login/verify-code`.
 */
export interface LoginChallenge {
  token: string
  expiresIn: number
}

/** What `/auth/me` and `/auth/login` return per membership — no branding here. */
export interface TenantSummary {
  id: string
  name: string
  legalName: string
  role: MembershipRole
}

/**
 * A membership and its tenant are one object in the API: `id` is the tenant id
 * and `role` is this user's role in it.
 */
export type Membership = TenantSummary

/** `/api/tenants/current` maps to a DTO — camelCase. The PATCH body is snake_case. */
export interface Tenant {
  id: string
  name: string
  legalName: string
  ownerName: string | null
  street: string | null
  zip: string | null
  city: string | null
  country: string
  vatId: string | null
  taxNumber: string | null
  registrationNumber: string | null
  email: string | null
  phone: string | null
  website: string | null
  iban: string | null
  bic: string | null
  bankName: string | null
  logoDocumentId: string | null
  defaultCurrency: string
  defaultPaymentDays: number
  invoiceFooter: string | null
  invoiceIntro: string | null
  invoiceColor: string
  /** `null`, solange noch keine Rechnungsvorlage gewählt wurde. */
  invoiceTemplate: InvoiceTemplateId | null
  /** Zahlungs-QR-Code auf Rechnungen — ab Werk an. */
  invoiceQrEnabled: boolean
  /** Konto, auf das Rechnungen zahlen; `null` = Freitext-Bankverbindung. */
  invoiceBankAccountId: string | null
  taxOffice: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  closure: ClosureState
}

/**
 * Kündigung und Aufbewahrung. A closed tenant is readable but not writable
 * until `retentionUntil` (§ 132 BAO), and is erased after it.
 */
export interface ClosureState {
  status: 'active' | 'closed'
  closureRequestedAt: string | null
  closedAt: string | null
  /** `YYYY-MM-DD`, null while the account is live. */
  retentionUntil: string | null
  retentionYearsRemaining: number
  /** True once the deadline has passed and the purge job may take it. */
  purgeable: boolean
}

// --- Abo ---------------------------------------------------------------------

/** Mirror of `modules/billing` `BillingState` in the API. */
export type BillingState =
  'exempt' | 'trialing' | 'trial_expired' | 'active' | 'past_due' | 'canceled'

/** Mirror of `modules/billing` `BillingStatusDto`. */
export interface BillingStatus {
  /** `false` on installations without Stripe: no banner, no lock. */
  enabled: boolean
  state: BillingState
  writable: boolean
  trialEndsAt: string | null
  trialDaysLeft: number | null
  periodEnd: string | null
  cancelAtPeriodEnd: boolean
  hasSubscription: boolean
  /** Net price of a subscription started now, in cents; `null` without billing. */
  priceNetCents: number | null
}

// --- Vertragsdokumente -------------------------------------------------------

export type LegalDocumentKey = 'agb' | 'datenschutz' | 'avv'

/** `/api/legal` — which documents exist and what this tenant accepted. */
export interface LegalDocumentStatus {
  key: LegalDocumentKey
  title: string
  summary: string
  required: boolean
  /** `YYYY-MM-DD` of the current version. */
  version: string
  contentHash: string
  /** Null while the *current* version has not been accepted. */
  acceptedAt: string | null
  acceptedVersion: string | null
  /** An older version was accepted and this one has not been. */
  outdated: boolean
}

export interface LegalStatus {
  documents: LegalDocumentStatus[]
  /** False while a required document is unaccepted. */
  complete: boolean
}

/** `/api/legal/:key` — the document text, as served and hashed by the API. */
export interface LegalDocumentContent {
  key: LegalDocumentKey
  title: string
  version: string
  contentHash: string
  markdown: string
}

/** `/api/fiscal-years` returns `{ items: FiscalYear[] }` with camelCase fields. */
export interface FiscalYear {
  year: number
  status: FiscalYearStatus
  isClosed: boolean
  vatScheme: VatScheme
  vatPeriodicity: VatPeriodicity
  taxationMethod: TaxationMethod
  profitMethod: ProfitMethod
  pauschalServiceRate: boolean
  priorYearTurnover: number | null
  lossCarryforward: number
  claimsGrundfreibetrag: boolean
  notes: string | null
  closedAt: string | null
  closedBy: string | null
  bookingCount: number
}

/** `/api/categories` maps to a DTO, so these arrive camelCased. */
export interface Category {
  id: string
  code: string
  name: string
  direction: CategoryDirection
  directionLabel: string
  e1aKz: string | null
  e1aLabel: string | null
  defaultTaxCode: string | null
  isAsset: boolean
  isSystem: boolean
  isActive: boolean
  privateShareDefault: number
  deductibleRate: number
  sortOrder: number
  usageCount: number
  description: string | null
  createdAt: string
  updatedAt: string
}

/** `/api/contacts` — mirrors `ContactDto` in `contacts/service.ts` field-for-field. */
export interface Contact {
  id: string
  kind: ContactKind
  /** Unternehmer or Privatperson — decides the VAT treatment abroad. */
  customerType: CustomerType
  customerTypeLabel: string
  number: string | null
  name: string
  contactPerson: string | null
  street: string | null
  zip: string | null
  city: string | null
  country: string
  countryScope: CountryScope
  vatId: string | null
  vatIdValid: boolean | null
  vatIdCheckedAt: string | null
  taxNumber: string | null
  email: string | null
  phone: string | null
  website: string | null
  iban: string | null
  paymentDays: number | null
  defaultTaxCode: string | null
  /** What the tax rules suggest for this country/UID combination. */
  suggestedTaxCode: string
  defaultCategoryId: string | null
  defaultCurrency: string
  language: string
  notes: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface SessionPayload {
  /** The API may spell this either way; `readAccessToken` normalises it. */
  accessToken?: string
  access_token?: string
  /** Absent from `/auth/me`, present everywhere a token is freshly minted. */
  expiresIn?: number
  user: CurrentUser
  memberships: Membership[]
  /** Tenant the token is scoped to — always present, never a bare user. */
  tenant: TenantSummary
}

export function readAccessToken(payload: {
  accessToken?: string
  access_token?: string
}): string | null {
  return payload.accessToken ?? payload.access_token ?? null
}
